/**
 * `useDepartments` — lookup list for the Unit editor (Phase 6B).
 *
 * Backed by `GET /agent/departments/` (staff-only). Cached for the
 * component tree's lifetime; refetch on demand via the query client.
 */

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

export const DepartmentSchema = z.object({
  id: z.number(),
  slug: z.string(),
  display_name: z.string(),
  description: z.string(),
  sort_order: z.number(),
});

export type Department = z.infer<typeof DepartmentSchema>;

const DepartmentsResponseSchema = z.object({
  departments: z.array(DepartmentSchema),
  total: z.number(),
});

export interface UseDepartmentsResult {
  departments: Department[];
  loading: boolean;
  error: string | undefined;
  refetch: () => void;
}

export const departmentsKeys = {
  all: ["departments"] as const,
  list: () => ["departments", "list"] as const,
};

const EMPTY_DEPARTMENTS: Department[] = [];

async function fetchDepartments(signal?: AbortSignal): Promise<Department[]> {
  const res = await apiFetch("/agent/departments/", { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const raw = (await res.json()) as unknown;
  return DepartmentsResponseSchema.parse(raw).departments;
}

export function useDepartments(): UseDepartmentsResult {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: departmentsKeys.list(),
    queryFn: ({ signal }) => fetchDepartments(signal),
  });

  const refetch = useCallback(() => {
    void qc.invalidateQueries({ queryKey: departmentsKeys.list() });
  }, [qc]);

  return {
    departments: query.data ?? EMPTY_DEPARTMENTS,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : String(query.error)
      : undefined,
    refetch,
  };
}
